/**
 * PositPaginationControls.tsx
 * 
 * Pagination controls component for the Posits listing.
 * Extracted from MyPosits.tsx to improve modularity and reusability.
 * Updated to maintain consistent positioning when navigating between pages.
 */

import React, { useCallback } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface PositPaginationControlsProps {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
}

const PositPaginationControls: React.FC<PositPaginationControlsProps> = ({
  currentPage,
  setCurrentPage,
  totalPages,
}) => {
  // Handle page changes without scrolling
  const handlePageChange = useCallback((page: number) => {
    // Prevent default scroll behavior
    const scrollPosition = window.scrollY;
    
    // Change the page
    setCurrentPage(page);
    
    // Use requestAnimationFrame to ensure the scroll position is maintained after DOM updates
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollPosition);
    });
  }, [setCurrentPage]);

  // Generate pagination items based on current page and total pages
  const getPaginationItems = () => {
    const items = [];
    
    // Always show first page
    items.push(
      <PaginationItem key="page-1">
        <PaginationLink
          isActive={currentPage === 1}
          onClick={(e) => {
            e.preventDefault();
            handlePageChange(1);
          }}
        >
          1
        </PaginationLink>
      </PaginationItem>
    );
    
    // Show ellipsis if there are more than 4 pages and we're not on pages 1-3
    if (totalPages > 4 && currentPage > 3) {
      items.push(
        <PaginationItem key="ellipsis-1">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    // Show previous, current, and next page around the current page
    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
      // Don't duplicate the first or last page which we always show
      if (i === 1 || i === totalPages) continue;
      
      items.push(
        <PaginationItem key={`page-${i}`}>
          <PaginationLink
            isActive={currentPage === i}
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(i);
            }}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    // Show ellipsis if there are more than 4 pages and we're not on the last 3 pages
    if (totalPages > 4 && currentPage < totalPages - 2) {
      items.push(
        <PaginationItem key="ellipsis-2">
          <PaginationEllipsis />
        </PaginationItem>
      );
    }
    
    // Always show last page if it's not the first page
    if (totalPages > 1) {
      items.push(
        <PaginationItem key={`page-${totalPages}`}>
          <PaginationLink
            isActive={currentPage === totalPages}
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(totalPages);
            }}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }
    
    return items;
  };

  // Don't render pagination if there's only one page
  if (totalPages <= 1) return null;

  return (
    <div className="h-16 mt-6">
      <Pagination className="sticky bottom-4">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(Math.max(1, currentPage - 1));
              }}
              className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
          
          {getPaginationItems()}
          
          <PaginationItem>
            <PaginationNext
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(Math.min(totalPages, currentPage + 1));
              }}
              className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
};

export default PositPaginationControls; 